/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useCallback, useEffect, useRef, useState } from 'react';
import loadLiveSessionSdk from '../utils/loadLiveSessionSdk';
import toastService from '../utils/toastService';
import { serviceRequestService } from '../services/serviceRequestService';
import {
  MAX_LIVE_SESSION_RECONNECTS,
  getLiveSessionReconnectAttempts,
  resetLiveSessionReconnectAttempts,
  setLiveSessionReconnectAttempts,
} from '../utils/liveSessionReconnect';

const ERROR_MESSAGES = {
  token_invalid: 'Could not start a live session connection. Please try again.',
  connect_failed: 'Could not connect to the live session server. Retrying...',
  connection_lost: 'Lost connection to the live session server. Retrying...',
  room_not_found: 'That session is no longer available.',
  room_full: 'That session already has a viewer.',
  media_denied: 'Camera/microphone access was denied.',
  not_joined: 'You need to join a session first.',
  server_error: 'The live session server reported an error.',
  recording_unsupported: 'Recording is not supported for this session.',
  recording_failed: 'Recording failed. Please try again.',
};

// Maps the SDK's connection-status-changed values onto the coarse server
// connection state the UI renders. Unlisted values (e.g. the initial
// 'connecting') leave the current state untouched.
const SERVER_STATUS_BY_CONNECTION_STATUS = {
  open: 'connected',
  connected: 'connected',
  reconnecting: 'reconnecting',
  closed: 'disconnected',
  disconnected: 'disconnected',
  failed: 'disconnected',
};

const RETRYING_ERROR_CODES = ['connect_failed', 'connection_lost'];
const RECONNECT_GAVE_UP_PATTERN = /reconnection failed/i;

const useLiveSession = (active, displayName, namespace) => {
  const clientRef = useRef(null);
  const joinedRoomRef = useRef(null);
  const pendingJoinRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState('idle');
  const [sessions, setSessions] = useState([]);
  const [joinedRoom, setJoinedRoom] = useState(null);
  const [micEnabled, setMicEnabled] = useState(false);
  const [camEnabled, setCamEnabled] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState('idle');
  const [recordingElapsedMs, setRecordingElapsedMs] = useState(0);
  const [recordingError, setRecordingError] = useState(null);
  const [recordingSupported, setRecordingSupported] = useState(false);
  const [authorized, setAuthorized] = useState(true);
  // Health of the WebSocket link to the live session server:
  // 'connecting' | 'connected' | 'reconnecting' | 'disconnected'.
  const [serverStatus, setServerStatus] = useState('connecting');
  // Manual reconnects: bumping the nonce re-runs the connection effect, which tears down
  // the dead client and creates a fresh one. Attempts are capped per login.
  const [reconnectNonce, setReconnectNonce] = useState(0);
  const [reconnectAttempts, setReconnectAttempts] = useState(getLiveSessionReconnectAttempts);
  const manualReconnectRef = useRef(false);
  // Whether the initial authorization/connection outcome has been determined --
  // consumers use this to hold off rendering (e.g. show a skeleton) until it's
  // known whether live sessions are available at all, avoiding a layout flicker.
  const [ready, setReady] = useState(!active);

  // Re-read the persisted count whenever live sessions (re)activate -- login() resets
  // it in storage before the user is set, so a fresh login starts from zero here.
  useEffect(() => {
    if (active) {
      setReconnectAttempts(getLiveSessionReconnectAttempts());
    }
  }, [active]);

  // A successful connection restores the full allowance of manual reconnects.
  useEffect(() => {
    if (serverStatus === 'connected') {
      resetLiveSessionReconnectAttempts();
      setReconnectAttempts(0);
    }
  }, [serverStatus]);

  useEffect(() => {
    if (!active) {
      setReady(true);
      return undefined;
    }

    // A manual reconnect keeps the current layout (no skeleton) and shows "reconnecting"
    // until the fresh client reports back.
    const isManualReconnect = manualReconnectRef.current;
    manualReconnectRef.current = false;
    if (isManualReconnect) {
      setServerStatus('reconnecting');
    } else {
      setReady(false);
    }
    let cancelled = false;
    let readyFallbackTimer = null;
    const unsubscribers = [];
    const markReady = () => {
      if (!cancelled) {
        setReady(true);
      }
    };
    const updateServerStatus = (next) => {
      if (cancelled) { return; }
      setServerStatus(next);
      // A list from a dead connection can't be joined -- drop it rather than show stale rooms.
      if (next === 'disconnected') {
        setSessions([]);
      }
    };

    const host = process.env.REACT_APP_LIVE_SESSION_HOST;

    loadLiveSessionSdk(host)
      .then((TruVideoLiveSession) => {
        if (cancelled) { return; }

        const client = TruVideoLiveSession.create({ host, namespace, displayName });
        clientRef.current = client;

        // isRecordingSupported() is a plain synchronous capability check, not event-driven --
        // snapshot it once right after client creation rather than exposing a callback.
        setRecordingSupported(!!client.isRecordingSupported?.());

        unsubscribers.push(client.on('connection-status-changed', ({ status }) => {
          setConnectionStatus(status);
          const next = SERVER_STATUS_BY_CONNECTION_STATUS[status];
          if (next) {
            updateServerStatus(next);
          }
        }));
        unsubscribers.push(client.on('sessions-updated', ({ sessions: updated }) => {
          setServerStatus('connected');
          setSessions(updated || []);
          markReady();
        }));
        unsubscribers.push(client.on('joined', ({ room }) => {
          joinedRoomRef.current = room;
          setJoinedRoom(room);

          // Update capability check after joining, as it may depend on the token/room configuration
          setRecordingSupported(!!client.isRecordingSupported?.());

          // Reflect the join immediately — don't wait for a fresh sessions-updated push.
          setSessions((prev) => prev.map((s) => (
            s.room === room ? { ...s, status: 'in_live_session' } : s
          )));

          // If the session being joined is tied to a Service Request, log it to that
          // request's activity timeline. Best-effort — never blocks or surfaces to the call UX.
          const pending = pendingJoinRef.current;
          pendingJoinRef.current = null;
          if (pending?.room === room && pending?.service_request_uid) {
            serviceRequestService
              .logLiveSessionJoined(pending.service_request_uid, {
                room,
                glassUserName: pending.creator_name,
              })
              .catch(() => {});
          }
        }));
        unsubscribers.push(client.on('left', () => {
          const room = joinedRoomRef.current;
          joinedRoomRef.current = null;
          setJoinedRoom(null);
          setMicEnabled(false);
          setCamEnabled(false);
          setRecordingStatus('idle');
          setRecordingElapsedMs(0);
          setRecordingError(null);
          if (room) {
            setSessions((prev) => prev.map((s) => (
              s.room === room ? { ...s, status: 'waiting_for_viewer', viewer_name: null } : s
            )));
          }
        }));
        unsubscribers.push(client.on('mic-state-changed', ({ enabled }) => {
          setMicEnabled(enabled);
        }));
        unsubscribers.push(client.on('cam-state-changed', ({ enabled }) => {
          setCamEnabled(enabled);
        }));
        unsubscribers.push(client.on('error', ({ code, message }) => {
          if (code === 'origin_not_allowed') {
            setAuthorized(false);
            markReady();
            return;
          }
          markReady();

          // The SDK retries a dropped/failed connection on its own and reports a final
          // "reconnection failed after N attempts" error once it gives up.
          if (message && RECONNECT_GAVE_UP_PATTERN.test(message)) {
            updateServerStatus('disconnected');
            return;
          } else if (RETRYING_ERROR_CODES.includes(code)) {
            updateServerStatus('reconnecting');
          }
          

          toastService.error(ERROR_MESSAGES[code] || message || 'Live session error');
        }));
        unsubscribers.push(client.on('recording-state-changed', ({ status, elapsedMs, error }) => {
          setRecordingStatus(status);
          setRecordingElapsedMs(elapsedMs || 0);
          setRecordingError(error || null);
        }));
        unsubscribers.push(client.on('recording-uploaded', () => {
          toastService.success('Recording uploaded successfully.');
        }));

        // Safety net in case the server never pushes an initial sessions-updated/error
        // event -- don't leave consumers stuck showing a skeleton indefinitely.
        readyFallbackTimer = setTimeout(markReady, 5000);
      })
      .catch(() => {
        if (!cancelled) {
          toastService.error('Failed to load the live session viewer.');
          updateServerStatus('disconnected');
          markReady();
        }
      });

    return () => {
      cancelled = true;
      if (readyFallbackTimer) {
        clearTimeout(readyFallbackTimer);
      }
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      if (clientRef.current) {
        clientRef.current.destroy();
        clientRef.current = null;
      }
      joinedRoomRef.current = null;
      pendingJoinRef.current = null;
      setConnectionStatus('idle');
      setSessions([]);
      setJoinedRoom(null);
      setMicEnabled(false);
      setCamEnabled(false);
      setRecordingStatus('idle');
      setRecordingElapsedMs(0);
      setRecordingError(null);
      setRecordingSupported(false);
      setAuthorized(true);
      setServerStatus('connecting');
    };
  }, [active, displayName, namespace, reconnectNonce]);

  const reconnect = useCallback(() => {
    const attempts = getLiveSessionReconnectAttempts();
    if (attempts >= MAX_LIVE_SESSION_RECONNECTS) {
      setReconnectAttempts(attempts);
      return;
    }
    setLiveSessionReconnectAttempts(attempts + 1);
    setReconnectAttempts(attempts + 1);
    manualReconnectRef.current = true;
    setReconnectNonce((n) => n + 1);
  }, []);

  const join = useCallback((roomOrSession) => {
    // Mic/camera stay muted/off at join (micEnabled/camEnabled default to false above);
    // the user opts in explicitly via toggleMic()/toggleCam().
    // Stash the full session object (when given one) so the 'joined' handler can
    // read its service_request_uid/creator_name once the SDK confirms the join.
    pendingJoinRef.current = (roomOrSession && typeof roomOrSession === 'object') ? roomOrSession : null;
    return clientRef.current?.joinSession(roomOrSession);
  }, []);
  const leave = useCallback(() => clientRef.current?.leaveSession(), []);
  const toggleMic = useCallback(() => clientRef.current?.toggleMic(), []);
  const toggleCam = useCallback(() => clientRef.current?.toggleCam(), []);
  const startRecording = useCallback(() => clientRef.current?.startRecording(), []);
  const stopRecording = useCallback(() => clientRef.current?.stopRecording(), []);
  const attachLocalVideo = useCallback((el) => clientRef.current?.attachLocalVideo(el), []);
  const attachRemoteVideo = useCallback((el) => clientRef.current?.attachRemoteVideo(el), []);

  return {
    connectionStatus,
    sessions,
    joinedRoom,
    micEnabled,
    camEnabled,
    recordingStatus,
    recordingElapsedMs,
    recordingError,
    recordingSupported,
    authorized,
    serverStatus,
    reconnect,
    reconnectAttemptsLeft: Math.max(0, MAX_LIVE_SESSION_RECONNECTS - reconnectAttempts),
    ready,
    join,
    leave,
    toggleMic,
    toggleCam,
    startRecording,
    stopRecording,
    attachLocalVideo,
    attachRemoteVideo,
  };
};

export default useLiveSession;
